import {
  buildInitiateCheckoutParams,
  buildPurchaseParams,
  syncMetaUserFromSession,
  trackMetaPageView,
  trackMetaPixelEvent,
} from '../../utils/metaPixel';

const PURCHASE_KEY_PREFIX = 'v2_tracking_purchase_';
const INITIATE_CHECKOUT_KEY_PREFIX = 'v2_tracking_initiate_checkout_';

function pushDataLayer(event, ecommerce = {}) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    ecommerce: {
      currency: 'INR',
      ...ecommerce,
    },
  });
}

/** Best-effort Trackocity hook when their global API is present. */
function trackTrackocity(eventName, payload = {}) {
  if (typeof window === 'undefined') return;
  try {
    if (typeof window.trackocity?.track === 'function') {
      window.trackocity.track(eventName, payload);
      return;
    }
    if (typeof window.Trackocity?.track === 'function') {
      window.Trackocity.track(eventName, payload);
    }
  } catch {
    // Trackocity pixel in index.html may still capture SPA navigations automatically.
  }
}

function cartLinesToGtmItems(products) {
  return (products || []).map((line, index) => ({
    item_id: String(line.productId ?? index),
    item_name: line.name || 'Item',
    price: Number(line.price) || 0,
    quantity: Number(line.quantity) || 0,
  }));
}

function receiptToGtmItems(receipt) {
  return cartLinesToGtmItems(receipt?.products);
}

function cartCheckoutSignature(cart) {
  const products = Array.isArray(cart?.products) ? cart.products : [];
  return products
    .map((p) => `${p.productId}:${Number(p.quantity) || 0}`)
    .sort()
    .join('|');
}

function cartNumItems(cart) {
  const products = Array.isArray(cart?.products) ? cart.products : [];
  return products.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
}

function cartContentIds(cart) {
  const products = Array.isArray(cart?.products) ? cart.products : [];
  return products.map((p) => String(p.productId)).filter(Boolean);
}

/**
 * @param {{ path?: string }} opts
 */
export function trackV2PageView({ path } = {}) {
  trackMetaPageView();
  pushDataLayer('page_view', {
    page_path: path || (typeof window !== 'undefined' ? window.location.pathname + window.location.search : ''),
  });
  trackTrackocity('PageView', { path });
}

/**
 * @param {{ cart: object, session?: { mobile?: string, user?: object } }} opts
 */
export function trackV2InitiateCheckout({ cart, session } = {}) {
  const signature = cartCheckoutSignature(cart);
  if (!signature) return;

  const storageKey = `${INITIATE_CHECKOUT_KEY_PREFIX}${signature}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
  } catch {
    // private mode / blocked storage
  }

  const value = Number(cart?.totalAmount) || 0;
  const numItems = cartNumItems(cart);
  const contentIds = cartContentIds(cart);

  if (session?.mobile) {
    syncMetaUserFromSession({
      mobile: session.mobile,
      user: session.user,
    });
  }

  const metaParams = buildInitiateCheckoutParams({ value, numItems, contentIds });
  trackMetaPixelEvent('InitiateCheckout', metaParams);

  pushDataLayer('begin_checkout', {
    value,
    num_items: numItems,
    items: cartLinesToGtmItems(cart?.products),
  });

  trackTrackocity('InitiateCheckout', {
    value,
    currency: 'INR',
    num_items: numItems,
    content_ids: contentIds,
  });

  try {
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // ignore
  }
}

/**
 * @param {{ orderId: string, receipt: object, session?: { mobile?: string, user?: object, addressName?: string } }} opts
 */
export function trackV2Purchase({ orderId, receipt, session } = {}) {
  if (!orderId || receipt?.total == null) return;

  const storageKey = `${PURCHASE_KEY_PREFIX}${orderId}`;
  try {
    if (sessionStorage.getItem(storageKey)) return;
  } catch {
    // ignore
  }

  if (session?.mobile) {
    syncMetaUserFromSession({
      mobile: session.mobile,
      user: session.user,
      addressName: session.addressName,
    });
  }

  const metaParams = buildPurchaseParams({ orderId, receipt });
  trackMetaPixelEvent('Purchase', metaParams);

  pushDataLayer('purchase', {
    transaction_id: String(orderId),
    value: metaParams.value,
    items: receiptToGtmItems(receipt),
  });

  trackTrackocity('Purchase', {
    order_id: String(orderId),
    value: metaParams.value,
    currency: 'INR',
    content_ids: metaParams.content_ids,
  });

  try {
    sessionStorage.setItem(storageKey, '1');
  } catch {
    // ignore
  }
}
